import React, { useMemo } from 'react';
import { Country, State, City } from 'country-state-city';
import { MapPin, Trash } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import SearchableSelect from '../../components/SearchableSelect';
import { useInactiveCountries } from '../../hooks/useInactiveCountries';

export const LocationRow = ({ loc, index, platformSettings, updateLocation, removeLocation, canRemove }) => {
  const countryEnabled = platformSettings.seller_country_selection !== false;
  const countries = useMemo(() =>
    Country.getAllCountries().map(c => ({ value: c.isoCode, label: `${c.flag} ${c.name}`, name: c.name })), []);
  const inactiveCountries = useInactiveCountries();
  const visibleCountries = useMemo(
    () => countries.filter(c => !inactiveCountries.has((c.name || '').toLowerCase())),
    [countries, inactiveCountries]
  );
  const effectiveCountry = countryEnabled ? loc.countryCode : 'IN';
  const states = useMemo(() => {
    if (!effectiveCountry) return [];
    return State.getStatesOfCountry(effectiveCountry).map(s => ({ value: s.isoCode, label: s.name }));
  }, [effectiveCountry]);
  const cities = useMemo(() => {
    if (!effectiveCountry || !loc.stateCode) return [];
    return City.getCitiesOfState(effectiveCountry, loc.stateCode).map(c => ({ value: c.name, label: c.name }));
  }, [effectiveCountry, loc.stateCode]);

  return (
    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
      {countryEnabled && (
        <div className="w-full sm:w-44">
          <SearchableSelect value={loc.countryCode}
            onChange={(val) => {
              const cn = Country.getCountryByCode(val)?.name || val;
              updateLocation(index, { countryCode: val, country: cn, stateCode: '', state: '', city: '', pincode: loc.pincode });
            }}
            options={visibleCountries} placeholder="Country" icon={MapPin} testId={`loc-country-${index}`} accentColor="emerald" />
        </div>
      )}
      <div className="w-full sm:w-40">
        <SearchableSelect value={loc.stateCode}
          onChange={(val) => {
            const sn = State.getStateByCodeAndCountry(val, effectiveCountry)?.name || val;
            updateLocation(index, { ...loc, stateCode: val, state: sn, city: '' });
          }}
          options={states} placeholder={countryEnabled ? (loc.countryCode ? 'State' : 'Country first') : 'State'} icon={MapPin}
          testId={`loc-state-${index}`} accentColor="emerald" disabled={countryEnabled && !loc.countryCode} />
      </div>
      <div className="w-full sm:w-40">
        <SearchableSelect value={loc.city} onChange={(val) => updateLocation(index, { ...loc, city: val })}
          options={cities} placeholder={loc.stateCode ? 'City' : 'State first'} icon={MapPin}
          testId={`loc-city-${index}`} accentColor="emerald" disabled={!loc.stateCode} />
      </div>
      <Input type="text" value={loc.pincode} onChange={(e) => updateLocation(index, { ...loc, pincode: e.target.value })}
        placeholder="Pin code" className="h-9 w-28 rounded-lg text-sm" data-testid={`loc-pin-${index}`} />
      {canRemove && (
        <button onClick={() => removeLocation(index)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`loc-remove-${index}`}>
          <Trash size={16} />
        </button>
      )}
    </div>
  );
};
